import * as React from 'react';
import { Timeline as FlowBiteTimeLine } from 'flowbite-react';

import { cn, getAssetSrc } from '@restworld/utils-common';
import { ArrowDownCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

const { Item, Content, Title, Body } = FlowBiteTimeLine;

export interface TimelineV2Props {
  timeLineSteps: {
    key?: number;
    title: string;
    subtitle?: string;
    checked?: boolean;
    isFlagged?: boolean;
    showDownIcon?: boolean;
    verticalLineStyle?: 'dashed' | 'solid';
    customContentElement?: React.ReactNode;
  }[];
}
export const TimelineV2 = ({ timeLineSteps }: TimelineV2Props) => {
  return (
    <FlowBiteTimeLineStyled>
      {timeLineSteps.map(
        ({ title, subtitle, checked, isFlagged, showDownIcon, customContentElement, verticalLineStyle }, i, list) => {
          return (
            <ItemStyled
              key={title}
              twstyle={[
                verticalLineStyle === 'dashed' ? 'border-dashed border-neutral' : '',
                i === list.length - 1 ? 'pb-0' : '',
              ]}
            >
              <FlowBiteTimeLinePointStyled
                theme={{}}
                icon={() =>
                  isFlagged ? (
                    <img src={getAssetSrc('/utils-assets/flag-checkmark.svg')} alt="" />
                  ) : showDownIcon ? (
                    <ArrowDownCircleIcon className="h-5 w-5 text-neutral-100" />
                  ) : checked ? (
                    <CheckCircleIcon className="h-5 w-5 text-purple-500" />
                  ) : (
                    <TimeLineIcon>{i + 1}</TimeLineIcon>
                  )
                }
              />
              {customContentElement || (
                <Content>
                  <TitleStyled>{title}</TitleStyled>
                  {subtitle ? <BodyStyled>{subtitle}</BodyStyled> : null}
                </Content>
              )}
            </ItemStyled>
          );
        }
      )}
    </FlowBiteTimeLineStyled>
  );
};
export default TimelineV2;

const FlowBiteTimeLineStyled = (props: React.ComponentProps<typeof FlowBiteTimeLine>) => (
  <FlowBiteTimeLine {...props} className={cn('border-none', props.className)} />
);
const FlowBiteTimeLinePointStyled = (props: React.ComponentProps<typeof FlowBiteTimeLine.Point>) => (
  <FlowBiteTimeLine.Point {...props} className={cn('[&_span]:bg-white', props.className)} />
);
const ItemStyled = ({ twstyle, className, ...props }: { twstyle?: string[] } & React.ComponentProps<typeof Item>) => (
  <Item
    // flowbite's Timeline.Item defaults to `mb-10 ml-6` (a 40px inter-step margin). Override the item
    // theme directly so flowbite emits `mb-0` instead — the step gap is then driven purely by pb-8,
    // matching master's styled(Item). (Fighting mb-10 from className needs a `!mb-0` utility that this
    // dev setup keeps failing to JIT-regenerate.)
    theme={{ root: { vertical: 'mb-0' } }}
    className={cn('pb-8 pl-6 mb-0 ml-0', 'border-l border-solid border-purple-500', ...(twstyle || []), className)}
    {...props}
  />
);
const TitleStyled = (props: React.ComponentProps<typeof Title>) => (
  <Title {...props} className={cn('text-base font-medium text-neutral-700', props.className)} />
);
const BodyStyled = (props: React.ComponentProps<typeof Body>) => <Body {...props} />;
const TimeLineIcon = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('', className)} {...props} />
);
